
import React, { useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import heroImage1 from '../assets/herosection/1_PV.jpeg';
import heroImage2 from '../assets/herosection/2_PV.png';
import heroImage3 from '../assets/herosection/3_PV.jpeg';
import heroImage4 from '../assets/herosection/4_Zähler.jpeg';
import heroImage5 from '../assets/herosection/5_Zähler.jpeg';
import heroImage6 from '../assets/herosection/6.Sonder.jpeg';

const HeroSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    duration: 50,
    dragFree: true
  });

  const autoplayRef = useRef<number | null>(null);

  useEffect(() => {
    if (emblaApi) {
      return () => {
        if (autoplayRef.current) {
          clearTimeout(autoplayRef.current);
        }
      };
    }
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      const autoplay = () => {
        if (emblaApi.canScrollNext()) {
          emblaApi.scrollNext();
        } else {
          emblaApi.scrollTo(0);
        }
        
        autoplayRef.current = window.setTimeout(() => {
          requestAnimationFrame(autoplay);
        }, 5000);
      };

      autoplayRef.current = window.setTimeout(() => {
        requestAnimationFrame(autoplay);
      }, 5000);
      
      return () => {
        if (autoplayRef.current) {
          clearTimeout(autoplayRef.current);
        }
      };
    }
  }, [emblaApi]);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Optimized animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const leftColumnVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const rightColumnVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.5,
        ease: "easeOut" 
      }
    }
  };

  return (
    <motion.section 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
      id="home" 
      className="relative bg-gradient-to-r from-primary/10 to-secondary/20 py-20"
    >
      <div className="container flex flex-col lg:flex-row items-center">
        <motion.div 
          variants={leftColumnVariants}
          className="lg:w-1/2 space-y-6 text-center lg:text-left mb-8 lg:mb-0"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight">
            Elektromeisterbetrieb<br />für Qualität & Sicherheit
          </h1>
          <p className="text-paragraph text-lg max-w-2xl mx-auto lg:mx-0 my-6">
            Von der initialen Bedarfsanalyse über die Beratung bis hin zur fachgerechten Durchführung, alles aus einer Hand.
          </p>
          <p className="text-paragraph text-lg max-w-2xl mx-auto lg:mx-0 my-6">
            Stöbern Sie in unserem hauseigenen Onlineshop und entdecken Sie eine große Auswahl an Notstrom-Umschaltern, Photovoltaik-Produkten und Elektrobedarf.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button 
              size="lg" 
              className="min-w-[250px] justify-center"
              onClick={() => scrollToSection('services')}
            >
              Dienstleistungen entdecken
            </Button>
            <Button 
              size="lg" 
              className="min-w-[250px] justify-center"
              onClick={() => window.open('https://www.dietronik-shop.de/39202120/startseite', '_blank', 'noopener,noreferrer')}
            >
              Zum Dietronik Shop
            </Button>
          </div>
        </motion.div>
        <motion.div 
          variants={rightColumnVariants}
          className="lg:w-1/2 flex justify-center lg:justify-end"
        >
          <div className="w-full max-w-xl relative">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                  {[heroImage1, heroImage2, heroImage3, heroImage4, heroImage5, heroImage6].map((image, index) => (
                    <div key={index} className="flex-[0_0_100%]">
                      <img 
                        src={image}
                        alt={`Dietronik Service Elektronik und Photovoltaik ${index + 1}`} 
                        className="w-full aspect-video object-cover rounded-md mb-4"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-primary/10 p-3 rounded">
                  <h3 className="font-medium text-primary mb-2 text-left">Schnelle Diagnose</h3>
                  <p className="text-paragraph text-sm text-left">Wartungs- und Reparaturservice im 24 Stunden Bereitschaftsdienst.<br /> Wir sind immer für Sie da!</p>
                </div>
                <div className="bg-primary/10 p-3 rounded">
                  <h3 className="font-medium text-primary mb-2 text-left">Garantierte Qualität</h3>
                  <p className="text-paragraph text-sm text-left">Zuverlässigkeit, Schnelligkeit und ein faires Preis-Leistungsverhältnis sind für uns selbstverständlich.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HeroSection;
